import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useMarkEngagementSeen } from "@/hooks/useUnseenEngagement";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowRight,
  BarChart3,
  FileText,
  Flame,
  Sparkles,
} from "lucide-react";
import { Area, BarChart, Bar, ComposedChart, Line, ReferenceLine, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { PromptTypesPieChart } from "@/components/PromptTypesPieChart";
import { FluencyStrengths } from "@/components/profile/FluencyStrengths";
import { startOfWeek, addDays, average } from "@/lib/dateUtils";
import { getScoreColor, ScoreDisplay } from "@/lib/scoreUtils";
import {
  WEEKLY_GOAL,
  getCurrentDailyStreak,
  getLongestDailyStreak,
  getCurrentWeeklyStreak,
  getLongestWeeklyStreak,
  buildStreakMaps,
} from "@/lib/streakUtils";

interface DashboardStats {
  daily_streak: number;
  weekly_streak: number;
  longest_daily_streak: number;
  longest_weekly_streak: number;
  prompts_this_week: number;
  prompts_last_week: number;
  prompts_last_30_days: number;
  total_prompts: number;
  total_points: number;
  points_last_30_days: number;
  avg_prompt_score: number | null;
  team_rank: number | null;
  team_member_count: number | null;
}

interface WeeklyData {
  week_start: string;
  count: number;
}

interface PromptTypeData {
  prompt_type: string;
  count: number;
}

interface CombinedStats {
  total_points: number;
  total_submissions: number;
  points_last_7_days: number;
  points_last_30_days: number;
  points_last_90_days: number;
  submissions_last_7_days: number;
  submissions_last_30_days: number;
}

interface ScoredItem {
  created_at: string | null;
  score_total_1_100: number | null;
  original_score_1_100: number | null;
}

interface FsrSnapshot {
  fsr_1_100: number;
  created_at: string;
  dimension_avgs_raw: Record<string, number> | null;
}

interface LeaderboardRow {
  user_id: string;
  full_name: string;
  total_points: number;
}

interface MomentumPoint {
  label: string;
  dateLabel: string;
  score: number;
  activity: number;
}

interface MomentumSummary {
  startLabel: string;
  startScore: number | null;
  cosmoScore: number | null;
  latestScore: number | null;
  hasOriginalScore: boolean;
}

function getProgressCopy(promptsThisWeek: number) {
  if (promptsThisWeek >= WEEKLY_GOAL) return "Weekly streak saved";
  const remaining = WEEKLY_GOAL - promptsThisWeek;
  return `${remaining} more prompt${remaining === 1 ? "" : "s"} to save your streak`;
}

function averageScore(items: ScoredItem[]) {
  const scores = items
    .map((item) => item.score_total_1_100)
    .filter((score): score is number => typeof score === "number");
  return average(scores);
}

function formatShortDate(dateString: string | null) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function roundedScore(score: number | null) {
  if (score === null) return "--";
  return Math.round(score).toString();
}

function formatExactPercentile(rank: number | null, memberCount: number | null) {
  if (!rank || !memberCount) return "--";
  return `Top ${Math.min(100, Math.max(1, Math.ceil((rank / memberCount) * 100)))}%`;
}

function buildMomentumJourney(scoredItems: ScoredItem[]): {
  points: MomentumPoint[];
  summary: MomentumSummary;
} {
  const validItems = scoredItems
    .filter((item): item is ScoredItem & { created_at: string; score_total_1_100: number } =>
      Boolean(item.created_at) && typeof item.score_total_1_100 === "number"
    )
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  if (validItems.length === 0) {
    return {
      points: [],
      summary: {
        startLabel: "First prompt",
        startScore: null,
        cosmoScore: null,
        latestScore: null,
        hasOriginalScore: false,
      },
    };
  }

  const firstItemIndex = validItems.findIndex((item) =>
    typeof item.original_score_1_100 === "number" &&
    item.original_score_1_100 <= item.score_total_1_100
  );
  const firstItem = firstItemIndex >= 0 ? validItems[firstItemIndex] : validItems[0];
  const laterStartIndex = firstItemIndex >= 0 ? firstItemIndex + 1 : 1;
  const hasOriginalScore = firstItemIndex >= 0;
  const points: MomentumPoint[] = [];

  if (hasOriginalScore) {
    points.push({
      label: "First prompt",
      dateLabel: formatShortDate(firstItem.created_at),
      score: firstItem.original_score_1_100 as number,
      activity: 1,
    });
    points.push({
      label: "With Cosmo",
      dateLabel: formatShortDate(firstItem.created_at),
      score: firstItem.score_total_1_100,
      activity: 1,
    });
  } else {
    points.push({
      label: "First scored prompt",
      dateLabel: formatShortDate(firstItem.created_at),
      score: firstItem.score_total_1_100,
      activity: 1,
    });
  }

  const laterItems = validItems.slice(laterStartIndex);
  const rollingPoints = laterItems.map((item, index) => {
    const windowStart = Math.max(0, index - 2);
    const windowScores = laterItems
      .slice(windowStart, index + 1)
      .map((windowItem) => windowItem.score_total_1_100);

    return {
      label: formatShortDate(item.created_at),
      dateLabel: formatShortDate(item.created_at),
      score: average(windowScores) ?? 0,
      activity: 1,
    };
  });

  const maxTrendPoints = 10;
  const bucketSize = Math.max(1, Math.ceil(rollingPoints.length / maxTrendPoints));
  for (let i = 0; i < rollingPoints.length; i += bucketSize) {
    const bucket = rollingPoints.slice(i, i + bucketSize);
    const lastPoint = bucket[bucket.length - 1];
    points.push({
      label: lastPoint.label,
      dateLabel: lastPoint.dateLabel,
      score: average(bucket.map((point) => point.score)) ?? 0,
      activity: bucket.length,
    });
  }

  return {
    points,
    summary: {
      startLabel: hasOriginalScore ? "First prompt" : "First scored prompt",
      startScore: hasOriginalScore ? firstItem.original_score_1_100 : firstItem.score_total_1_100,
      cosmoScore: hasOriginalScore ? firstItem.score_total_1_100 : null,
      latestScore: points[points.length - 1]?.score ?? null,
      hasOriginalScore,
    },
  };
}

function buildDashboardStats({
  scoredItems,
  combinedStats,
  leaderboard,
  userId,
}: {
  scoredItems: ScoredItem[];
  combinedStats: CombinedStats | null;
  leaderboard: LeaderboardRow[];
  userId: string;
}): DashboardStats {
  const now = new Date();
  const thisWeekStart = startOfWeek(now);
  const nextWeekStart = addDays(thisWeekStart, 7);
  const lastWeekStart = addDays(thisWeekStart, -7);
  const validItems = scoredItems.filter((item) => item.created_at);
  const timestamps = validItems.map((item) => new Date(item.created_at as string));
  const { dayKeys, weekCounts } = buildStreakMaps(timestamps);

  const thisWeekItems = validItems.filter((item) => {
    const createdAt = new Date(item.created_at as string);
    return createdAt >= thisWeekStart && createdAt < nextWeekStart;
  });

  const lastWeekItems = validItems.filter((item) => {
    const createdAt = new Date(item.created_at as string);
    return createdAt >= lastWeekStart && createdAt < thisWeekStart;
  });

  const leaderboardIndex = leaderboard.findIndex((row) => row.user_id === userId);
  return {
    daily_streak: getCurrentDailyStreak(dayKeys),
    weekly_streak: getCurrentWeeklyStreak(weekCounts),
    longest_daily_streak: getLongestDailyStreak(dayKeys),
    longest_weekly_streak: getLongestWeeklyStreak(weekCounts),
    prompts_this_week: thisWeekItems.length,
    prompts_last_week: lastWeekItems.length,
    prompts_last_30_days: combinedStats?.submissions_last_30_days || 0,
    total_prompts: combinedStats?.total_submissions || validItems.length,
    total_points: combinedStats?.total_points || 0,
    points_last_30_days: combinedStats?.points_last_30_days || 0,
    avg_prompt_score: averageScore(validItems),
    team_rank: leaderboardIndex >= 0 ? leaderboardIndex + 1 : null,
    team_member_count: leaderboard.length || null,
  };
}

export default function Stats() {
  const { user, membership } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardStats | null>(null);
  const [weeklyData, setWeeklyData] = useState<WeeklyData[]>([]);
  const [promptTypes, setPromptTypes] = useState<PromptTypeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dimensionAvgs, setDimensionAvgs] = useState<Record<string, number> | null>(null);
  const [scoredSubmissionCount, setScoredSubmissionCount] = useState<number>(0);
  const [momentumPoints, setMomentumPoints] = useState<MomentumPoint[]>([]);
  const [momentumSummary, setMomentumSummary] = useState<MomentumSummary>({
    startLabel: "First prompt",
    startScore: null,
    cosmoScore: null,
    latestScore: null,
    hasOriginalScore: false,
  });

  const markEngagementSeen = useMarkEngagementSeen();
  const hasMarkedSeen = useRef(false);

  useEffect(() => {
    if (user && !hasMarkedSeen.current) {
      hasMarkedSeen.current = true;
      markEngagementSeen.mutate(user.id);
    }
  }, [user, markEngagementSeen]);

  useEffect(() => {
    if (user) {
      fetchAllData();
    }
  }, [user, membership?.org_id]);

  const fetchAllData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const leaderboardPromise = membership?.org_id
        ? supabase.rpc("get_org_points_leaderboard", {
            _org_id: membership.org_id,
            _limit: 500,
          })
        : Promise.resolve({ data: [], error: null });

      const [
        weeklyRes,
        typesRes,
        combinedStatsRes,
        scoredItemsRes,
        fsrSnapshotsRes,
        leaderboardRes,
      ] = await Promise.all([
        supabase.rpc("get_user_combined_weekly_frequency", { _user_id: user.id }),
        supabase.rpc("get_user_combined_prompt_types", { _user_id: user.id }),
        supabase.rpc("get_user_stats", { _user_id: user.id }),
        supabase
          .from("v_scored_items")
          .select("created_at, score_total_1_100, original_score_1_100")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5000),
        supabase
          .from("fluency_snapshots")
          .select("fsr_1_100, created_at, dimension_avgs_raw")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(2),
        leaderboardPromise,
      ]);

      if (weeklyRes.error) console.error("Error fetching weekly frequency:", weeklyRes.error);
      if (typesRes.error) console.error("Error fetching prompt types:", typesRes.error);
      if (combinedStatsRes.error) console.error("Error fetching user stats:", combinedStatsRes.error);
      if (scoredItemsRes.error) console.error("Error fetching scored items:", scoredItemsRes.error);
      if (fsrSnapshotsRes.error) console.error("Error fetching fluency strengths:", fsrSnapshotsRes.error);
      if (leaderboardRes.error) console.error("Error fetching points leaderboard:", leaderboardRes.error);

      const momentumJourney = buildMomentumJourney(scoredItemsRes.data || []);
      setDashboard(buildDashboardStats({
        scoredItems: scoredItemsRes.data || [],
        combinedStats: combinedStatsRes.data?.[0] || null,
        leaderboard: leaderboardRes.data || [],
        userId: user.id,
      }));
      setMomentumPoints(momentumJourney.points);
      setMomentumSummary(momentumJourney.summary);
      setWeeklyData(weeklyRes.data || []);
      setPromptTypes(typesRes.data || []);
      setDimensionAvgs((fsrSnapshotsRes.data?.[0]?.dimension_avgs_raw as Record<string, number> | null) || null);
      setScoredSubmissionCount(combinedStatsRes.data?.[0]?.total_submissions || 0);
    } catch (error) {
      console.error("Error fetching stats data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-4 md:p-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid md:grid-cols-4 gap-4">
            <Skeleton className="h-36" />
            <Skeleton className="h-36" />
            <Skeleton className="h-36" />
            <Skeleton className="h-36" />
          </div>
          <Skeleton className="h-64 w-full" />
          <div className="grid md:grid-cols-2 gap-6">
            <Skeleton className="h-72" />
            <Skeleton className="h-72" />
          </div>
        </div>
      </MainLayout>
    );
  }

  const safeDashboard: DashboardStats = dashboard || {
    daily_streak: 0,
    weekly_streak: 0,
    longest_daily_streak: 0,
    longest_weekly_streak: 0,
    prompts_this_week: 0,
    prompts_last_week: 0,
    prompts_last_30_days: 0,
    total_prompts: 0,
    total_points: 0,
    points_last_30_days: 0,
    avg_prompt_score: null,
    team_rank: null,
    team_member_count: null,
  };

  const weeklyProgress = Math.min(safeDashboard.prompts_this_week, WEEKLY_GOAL);
  const weeklyProgressPercent = (weeklyProgress / WEEKLY_GOAL) * 100;
  const onPace = safeDashboard.prompts_this_week >= WEEKLY_GOAL;

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-bold">My Stats</h1>
            <p className="text-muted-foreground mt-1">Track your prompting habit, quality, and momentum.</p>
          </div>
          <Button onClick={() => navigate("/prompt")} variant="accent" className="gap-2 self-start">
            Build-A-Prompt
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.7fr)]">
          <Card className="overflow-hidden border-primary/25 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Prompt Momentum
              </CardTitle>
              <CardDescription>
                {momentumSummary.hasOriginalScore && momentumSummary.cosmoScore !== null
                  ? `Your first prompt started at ${roundedScore(momentumSummary.startScore)}. With Cosmo, it jumped to ${roundedScore(momentumSummary.cosmoScore)}.`
                  : "Track your prompt scores, points, and rank over time."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {momentumPoints.length > 0 ? (
                <div className="grid gap-4 xl:grid-cols-[190px_minmax(0,1fr)] xl:items-center">
                  <div className="grid grid-cols-2 gap-2 xl:grid-cols-1">
                    <div className="rounded-lg border border-border/70 bg-background/70 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Current Trend</p>
                      <div className="mt-1">
                        <ScoreDisplay score={momentumSummary.latestScore} size="lg" />
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/70 bg-background/70 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Total Prompts</p>
                      <p className="mt-1 text-2xl font-bold leading-none tabular-nums">{safeDashboard.total_prompts}</p>
                    </div>
                    <div className="rounded-lg border border-border/70 bg-background/70 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Total Points</p>
                      <p className="mt-1 text-2xl font-bold leading-none tabular-nums">
                        {safeDashboard.total_points.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/70 bg-background/70 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Your Rank</p>
                      <p className="mt-1 text-2xl font-bold leading-none tabular-nums">
                        {formatExactPercentile(safeDashboard.team_rank, safeDashboard.team_member_count)}
                      </p>
                    </div>
                  </div>

                  <div className="h-[220px] min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={momentumPoints} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="promptMomentumFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                          interval="preserveStartEnd"
                        />
                        <YAxis yAxisId="score" domain={[0, 100]} hide />
                        <YAxis yAxisId="activity" hide />
                        <Tooltip
                          cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }}
                          contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "8px",
                          }}
                          formatter={(value, name) => {
                            const numericValue = Number(value);
                            if (name === "score") return [`${Math.round(numericValue)}/100`, "Score trend"];
                            return [`${numericValue} prompt${numericValue === 1 ? "" : "s"}`, "Activity"];
                          }}
                        />
                        <Bar
                          yAxisId="activity"
                          dataKey="activity"
                          barSize={18}
                          fill="hsl(var(--muted))"
                          opacity={0.55}
                          radius={[4, 4, 0, 0]}
                        />
                        <Area
                          yAxisId="score"
                          type="monotone"
                          dataKey="score"
                          stroke="none"
                          fill="url(#promptMomentumFill)"
                        />
                        <Line
                          yAxisId="score"
                          type="monotone"
                          dataKey="score"
                          stroke="hsl(var(--primary))"
                          strokeWidth={3}
                          dot={{ r: 3, fill: "hsl(var(--primary))", strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 2 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                  <p>Build your first prompt to start your momentum chart.</p>
                  <Button onClick={() => navigate("/prompt")} variant="accent" size="sm">
                    Build-A-Prompt
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border bg-card/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-5 w-5 text-primary" />
                Streaks
              </CardTitle>
              <CardDescription>Keep the habit going.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex min-h-[96px] flex-col justify-between rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-sm text-muted-foreground">Daily Streak</p>
                  <div>
                    <p className="text-3xl font-bold leading-none tabular-nums">{safeDashboard.daily_streak}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Best daily: {safeDashboard.longest_daily_streak}</p>
                  </div>
                </div>
                <div className="flex min-h-[96px] flex-col justify-between rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-sm text-muted-foreground">Weekly streak</p>
                  <div>
                    <p className="text-3xl font-bold leading-none tabular-nums">{safeDashboard.weekly_streak}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Best weekly: {safeDashboard.longest_weekly_streak}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{safeDashboard.prompts_this_week} / {WEEKLY_GOAL} this week</span>
                  <span className="text-muted-foreground">{onPace ? "Saved" : "Keep it going"}</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center gap-2">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${
                        safeDashboard.prompts_this_week >= step
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-muted text-muted-foreground"
                      }`}>
                        {step}
                      </div>
                      {step < 3 ? (
                        <div className={`h-1 flex-1 rounded-full ${
                          safeDashboard.prompts_this_week > step ? "bg-primary" : "bg-muted"
                        }`} />
                      ) : null}
                    </div>
                  ))}
                </div>
                <Progress value={weeklyProgressPercent} />
                <p className="text-sm text-muted-foreground">{getProgressCopy(safeDashboard.prompts_this_week)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <h2 className="mb-4 text-xl font-semibold">Improve Your Prompting</h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <FluencyStrengths dimensionAvgs={dimensionAvgs} scoredCount={scoredSubmissionCount} />
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Prompt Types
                </CardTitle>
                <CardDescription>Patterns in what you write</CardDescription>
              </CardHeader>
              <CardContent>
                <PromptTypesPieChart data={promptTypes} height={250} />
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid gap-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Prompt Activity
              </CardTitle>
              <CardDescription>Weekly prompt volume with a 3-prompt streak target</CardDescription>
            </CardHeader>
            <CardContent>
              {weeklyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={weeklyData}>
                    <XAxis
                      dataKey="week_start"
                      tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(val) =>
                        new Date(val).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                      }
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }}
                      labelFormatter={(val) => new Date(val).toLocaleDateString()}
                    />
                    <ReferenceLine y={WEEKLY_GOAL} stroke="hsl(var(--primary))" strokeDasharray="4 4" />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex flex-col items-center justify-center gap-3 text-muted-foreground">
                  <p>No prompt activity yet</p>
                  <Button onClick={() => navigate("/prompt")} variant="accent" size="sm">
                    Build-A-Prompt
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
